
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Save } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { AlertRule, AlertSettings, createDefaultAlertRule } from '@/types/alertSettings';
import { defaultAlertSettings } from '@/data/defaultSchoolData';
import { AlertSection } from './AlertSection';

interface AlertSettingsCardProps {
  settings: any;
  updateSettings: (updates: any) => Promise<void>;
}

export const AlertSettingsCard = ({ settings, updateSettings }: AlertSettingsCardProps) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [alertSettings, setAlertSettings] = useState<AlertSettings>({
    absence_alerts: [],
    late_alerts: []
  });

  // إعداد التنبيهات الافتراضية باستخدام البيانات الافتراضية المحفوظة
  const createDefaultAbsenceAlerts = (): AlertRule[] => 
    defaultAlertSettings.absence_alerts.map(alert => ({
      ...alert,
      id: `${alert.id}_${Date.now()}`
    }));

  const createDefaultLateAlerts = (): AlertRule[] => 
    defaultAlertSettings.late_alerts.map(alert => ({
      ...alert,
      id: `${alert.id}_${Date.now()}`
    }));

  // تحويل الإعدادات القديمة إلى النظام الجديد
  useEffect(() => {
    if (settings) {
      const convertedSettings: AlertSettings = {
        absence_alerts: createDefaultAbsenceAlerts(),
        late_alerts: createDefaultLateAlerts()
      };

      if (settings.alert_rules) {
        // النظام الجديد موجود - التحقق من نوع البيانات
        let alertRules = settings.alert_rules;
        
        // إذا كانت البيانات عبارة عن نص JSON، نحولها إلى object
        if (typeof alertRules === 'string') {
          try {
            alertRules = JSON.parse(alertRules);
          } catch (error) {
            console.error('خطأ في تحليل alert_rules:', error);
            alertRules = { absence_alerts: createDefaultAbsenceAlerts(), late_alerts: [] };
          }
        }
        
        // استخدام التنبيهات الجديدة إذا لم تكن موجودة أو كانت فارغة
        convertedSettings.absence_alerts = alertRules.absence_alerts && alertRules.absence_alerts.length > 0 
          ? alertRules.absence_alerts 
          : createDefaultAbsenceAlerts();
        convertedSettings.late_alerts = alertRules.late_alerts && alertRules.late_alerts.length > 0 
          ? alertRules.late_alerts 
          : createDefaultLateAlerts();
      }

      setAlertSettings(convertedSettings);
    }
  }, [settings]);

  const handleSave = async () => {
    setLoading(true);
    try {
      // console.log('💾 حفظ إعدادات التنبيهات:', alertSettings);
      
      await updateSettings({
        alert_rules: alertSettings
      });
      
      // console.log('✅ تم حفظ إعدادات التنبيهات بنجاح');
      
      // تحديث الحالة المحلية فوراً لضمان عرض التغييرات
      setAlertSettings(alertSettings);
      
      toast({
        title: "تم الحفظ بنجاح",
        description: "تم تحديث إعدادات التنبيهات بنجاح",
      });
    } catch (error) {
      console.error('❌ خطأ في حفظ إعدادات التنبيهات:', error);
      
      toast({
        title: "خطأ في الحفظ",
        description: "لم نتمكن من حفظ إعدادات التنبيهات",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const addAlert = (type: 'absence' | 'late') => {
    const newThreshold = type === 'absence' ? 5 : 5;
    const newAlert = createDefaultAlertRule(type, newThreshold);
    
    setAlertSettings(prev => ({
      ...prev,
      [`${type}_alerts`]: [...prev[`${type}_alerts`], newAlert]
    }));
  };

  const updateAlert = (type: 'absence' | 'late', updatedRule: AlertRule) => {
    setAlertSettings(prev => ({
      ...prev,
      [`${type}_alerts`]: prev[`${type}_alerts`].map(rule => 
        rule.id === updatedRule.id ? updatedRule : rule
      )
    }));
  };

  const deleteAlert = (type: 'absence' | 'late', ruleId: string) => {
    setAlertSettings(prev => ({
      ...prev,
      [`${type}_alerts`]: prev[`${type}_alerts`].filter(rule => rule.id !== ruleId)
    }));
  };

  return (
    <Card className="bg-white/90 backdrop-blur-sm border-teal-200/50 shadow-lg" dir="rtl">
      <CardHeader className="pb-4 text-right bg-orange-50/80 rounded-t-lg" style={{ backgroundColor: '#11a7a2' }}>
        <CardTitle className="flex items-center gap-2 text-white justify-start text-lg">
          <AlertTriangle className="h-5 w-5 text-white" />
          <span>إعدادات التنبيهات المتقدمة</span>
        </CardTitle>
        <p className="text-sm text-white/90 text-right">
          أضف تنبيهات متعددة للغياب والتأخر بعتبات مختلفة
        </p>
      </CardHeader>
      <CardContent className="space-y-6 text-right pt-6">
        {/* تنبيهات الغياب */}
        <AlertSection
          type="absence"
          alerts={alertSettings.absence_alerts}
          onAddAlert={() => addAlert('absence')}
          onUpdateAlert={(updatedRule) => updateAlert('absence', updatedRule)}
          onDeleteAlert={(ruleId) => deleteAlert('absence', ruleId)}
        />

        {/* تنبيهات التأخر */}
        <AlertSection
          type="late"
          alerts={alertSettings.late_alerts}
          onAddAlert={() => addAlert('late')}
          onUpdateAlert={(updatedRule) => updateAlert('late', updatedRule)}
          onDeleteAlert={(ruleId) => deleteAlert('late', ruleId)}
        />

        {/* زر الحفظ */}
        <div className="pt-4 border-t border-border flex justify-end">
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-secondary hover:bg-secondary/90 text-secondary-foreground"
          >
            <Save className="h-4 w-4 ml-2" />
            {loading ? 'جاري الحفظ...' : 'حفظ إعدادات التنبيهات'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
